/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import Placeholder from "@tiptap/extension-placeholder";
import { BubbleMenu, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { motion } from "framer-motion";
import React, { useRef } from "react";
import { FaBold, FaItalic, FaStrikethrough, FaUnderline } from "react-icons/fa";
import { useRecoilValue } from "recoil";
import { UserAtom } from "../atoms/UserAtom";
import { Underline } from "./NewEmail/underline";

const Container = styled.div<{ isFocused: boolean }>`
  outline: none;
  padding: 9px;
  border: none;
  resize: none;
  border: 1px solid #dddddd;
  border-radius: 5px;
  min-height: 40px;
  width: 100%;

  /* Placeholder (at the top) */
  .ProseMirror p.is-editor-empty:first-of-type::before {
    content: attr(data-placeholder);
    float: left;
    pointer-events: none;
    height: 0;
    font-size: 14px;
    letter-spacing: 0.16px;
    color: #979797;
  }

  p {
    margin: 0 !important;
  }

  ${(props) =>
    props.isFocused &&
    css`
      box-shadow: 0px 0px 6px #fb972e4d;
      border: 1px solid #fb972e;
    `}

  transition: box-shadow 0.3s, border 0.3s;
`;

const Buttons = styled.div`
  border-radius: 4px;
  display: flex;
  flex-direction: row;
  padding: 0 3px;
  height: 36px;
  align-items: center;
  justify-content: center;
  background-color: #fb972e;
  box-shadow: 0px 3px 15px #00000029;

  button:not(.link) {
    border: none;
    background: none;
    font-size: 1rem;
    align-items: center;
    display: flex;
    justify-content: center;
    color: #fff;
    opacity: 0.5;
    font-weight: 500;
    height: 30px;
    width: 30px;
    border-radius: 4px;
    cursor: pointer;

    &.is-active {
      opacity: 1;
    }
  }
`;

interface Props {
  value: string;
  onChange: (content: string) => void;
  placeholder: string;
}

export default function StyledRichInput(props: Props): JSX.Element {
  const user = useRecoilValue(UserAtom);
  const colorLinkRef = useRef<string>(
    user?.emailOptions?.defaultModeColorLink || "#000000"
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bold: {
          HTMLAttributes: {
            style: "font-size: inherit;",
          },
        },
        italic: {
          HTMLAttributes: {
            style: "font-size: inherit;",
          },
        },
        strike: {
          HTMLAttributes: {
            style: "font-size: inherit;",
          },
        },
      }),
      Underline.configure({
        HTMLAttributes: {
          style: "font-size: inherit;",
          color: colorLinkRef.current,
        },
      }),
      Placeholder.configure({
        placeholder: props.placeholder,
      }),
    ],
    content: props.value,
    onUpdate: (content) => {
      props.onChange(content.editor.getHTML());
    },
  });

  return (
    <Container isFocused={Boolean(editor?.isFocused)}>
      {editor && (
        <BubbleMenu editor={editor} tippyOptions={{ duration: 100 }}>
          <Buttons>
            <motion.button
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={editor.isActive("bold") ? "is-active" : ""}
            >
              <FaBold />
            </motion.button>
            <motion.button
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={editor.isActive("italic") ? "is-active" : ""}
            >
              <FaItalic />
            </motion.button>
            <motion.button
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={editor.isActive("underline") ? "is-active" : ""}
            >
              <FaUnderline style={{ marginTop: 2 }} />
            </motion.button>

            <motion.button
              onClick={() => editor.chain().focus().toggleStrike().run()}
              className={editor.isActive("strike") ? "is-active" : ""}
            >
              <FaStrikethrough />
            </motion.button>
          </Buttons>
        </BubbleMenu>
      )}
      <EditorContent editor={editor} />
    </Container>
  );
}
